import React from 'react'

/**
 * Iframe that keeps a stable `src` when only auth token query params change.
 * Parent Redux token refreshes post the new tokens via postMessage instead of reloading.
 */
const AUTH_QUERY_PARAMS = ['token', 'access_token']

function urlWithoutAuthParams(url) {
  try {
    const parsed = new URL(url)
    AUTH_QUERY_PARAMS.forEach((param) => parsed.searchParams.delete(param))
    return parsed.toString()
  } catch {
    return String(url ?? '')
  }
}

function authParamsFromUrl(url) {
  try {
    const parsed = new URL(url)
    return {
      token: parsed.searchParams.get('token'),
      accessToken: parsed.searchParams.get('access_token'),
    }
  } catch {
    return { token: null, accessToken: null }
  }
}

function postAuthToken(iframe, { token, accessToken }) {
  if (!iframe?.contentWindow) return

  const trimmedToken = String(token ?? '').trim()
  const trimmedAccessToken = String(accessToken ?? '').trim()
  if (!trimmedToken && !trimmedAccessToken) return

  iframe.contentWindow.postMessage(
    {
      source: 'ops-dashboard',
      type: 'AUTH_TOKEN',
      ...(trimmedToken && { token: trimmedToken }),
      ...(trimmedAccessToken && { access_token: trimmedAccessToken }),
    },
    '*',
  )
}

export default function StableAppIframe({ src, title, id }) {
  const iframeRef = React.useRef(null)
  const identityRef = React.useRef(urlWithoutAuthParams(src))
  const [lockedSrc, setLockedSrc] = React.useState(src)

  React.useEffect(() => {
    const nextIdentity = urlWithoutAuthParams(src)
    if (nextIdentity !== identityRef.current) {
      identityRef.current = nextIdentity
      setLockedSrc(src)
    }
  }, [src])

  React.useEffect(() => {
    postAuthToken(iframeRef.current, authParamsFromUrl(src))
  }, [src, lockedSrc])

  return (
    <iframe
      ref={iframeRef}
      src={lockedSrc}
      title={title}
      id={id}
      style={{ flexGrow: 1, border: 0 }}
      onLoad={() => {
        postAuthToken(iframeRef.current, authParamsFromUrl(src))
      }}
    />
  )
}
